import { FC, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useLayout from 'hooks/useLayout'
import useAuth from 'hooks/useAuth'
import Avatar from '@/components/user/Avatar'
import { ReactComponent as AccountIcon } from 'assets/icons/account_circle.svg'
import { ReactComponent as GroupIcon } from 'assets/icons/group.svg'
import { ReactComponent as LogoutIcon } from 'assets/icons/logout.svg'

const linkRowClasses = `
    grid
    grid-cols-[min-content_auto]
    h-10
    items-center
    gap-2
    text-base
    cursor-pointer
    px-4
    bg-white
    rounded-md
    border
    border-gray-400
    [&>svg]:fill-black
    lg:hover:bg-blue-100
`

interface AccountLinkRowProps {
    to?: string
    text: string
    icon: ReactNode
    onClick?: () => void
}

const AccountLinkRow: FC<AccountLinkRowProps> = ({ to, text, icon, onClick }) => {
    const { closeDialog } = useLayout()

    const handleClick = () => {
        onClick?.()
        closeDialog()
    }

    if (to) {
        return (
            <Link to={to} className={linkRowClasses} onClick={handleClick}>
                {icon}
                <span className="text-black whitespace-nowrap select-none">{text}</span>
            </Link>
        )
    }

    return (
        <button type="button" className={`${linkRowClasses} w-full text-left`} onClick={handleClick}>
            {icon}
            <span className="text-black whitespace-nowrap select-none">{text}</span>
        </button>
    )
}

const AccountMenu = () => {
    const { t } = useTranslation()
    const { accountPublicData, logout } = useAuth()

    const greeting = accountPublicData?.other.isComplete
        ? `${t('hello')} ${accountPublicData?.name}!`
        : t('profileMenuGreetingAnonymous')

    return (
        <div className="flex flex-col gap-y-6 min-w-[16rem] pb-4">
            <div className="flex flex-col items-center gap-y-2">
                <Avatar
                    avatarUrl={accountPublicData?.other.avatarUrl}
                    name={accountPublicData?.other.isComplete ? accountPublicData?.name : undefined}
                    hasAvatar={accountPublicData?.other.hasAvatar}
                    size="lg"
                    avatarColor={accountPublicData?.other.avatarColor}
                />
                <div className="font-bold text-black">{greeting}</div>
            </div>
            <div className="flex flex-col gap-y-2">
                <AccountLinkRow to="/account" icon={<AccountIcon />} text={t('account')} />
                <AccountLinkRow to="/access-invitations" icon={<GroupIcon />} text={t('buttonAccessInvitations')} />
                <AccountLinkRow icon={<LogoutIcon />} text={t('logout')} onClick={logout} />
            </div>
        </div>
    )
}

export default AccountMenu
